import React from "react"

import { format } from "date-fns"
import { ExternalLink } from "lucide-react"

import CostBreakdownCard, { type CostLine } from "@/components/shared/CostBreakdownCard"
import { Button } from "@/components/ui/button"
import { TableCell, TableRow } from "@/components/ui/table"
import { useAuth } from "@/contexts/AuthContext"
import { instanceStopForecast, type PlanningRouteCost } from "@/lib/planningRoutes"
import { formatChargingMilesBreakdown } from "@/lib/routeCostCalculation"
import { getStopTypeColor, getStopTypeIcon } from "@/lib/slotTypeDisplay"
import { cn } from "@/lib/utils"
import type { PlannedRoute, StopInstance } from "@/types/collections"

import type { RouteMetrics } from "./useRouteMetrics"

interface PlanningRouteExpandedRowProps {
  route: PlannedRoute
  metrics: RouteMetrics
  onEdit: (e: React.MouseEvent) => void
}

const PlanningRouteExpandedRow: React.FC<PlanningRouteExpandedRowProps> = ({
  route,
  metrics,
  onEdit,
}) => {
  const { can } = useAuth()
  const ri = metrics.instance
  const cost = can.costs ? metrics.cost : null

  return (
    <TableRow className="bg-palette-jungleGreen000/30">
      <TableCell colSpan={14} className="p-0">
        <div className="px-6 py-3 border-t border-palette-jungleGreen100">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold text-palette-jungleGreen700 uppercase tracking-wide">
              Route Stops ({route.stops.length})
            </h4>
            {can.editRoutes && (
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-xs bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
                onClick={onEdit}
              >
                <ExternalLink size={10} className="mr-1" />
                Edit in Timeline
              </Button>
            )}
          </div>

          {route.stops.length === 0 ? (
            <div className="text-sm text-palette-jungleGreen500 italic py-2">
              No stops planned for this route
            </div>
          ) : (
            <div className="space-y-0 pt-1">
              {route.stops
                .sort((a, b) => a.sequence - b.sequence)
                .map((stop, index) => {
                  const instanceStop = ri?.stops?.find(
                    (s: StopInstance) => s.location_id === stop.locationId,
                  )
                  const vol = instanceStop?.volume_actual ?? instanceStopForecast(ri, stop)
                  const isActual = instanceStop?.volume_actual != null
                  return (
                    <div key={stop.id} className="flex gap-2">
                      <div className="flex flex-col items-center">
                        <div
                          className={cn(
                            "w-5 h-5 rounded-full flex items-center justify-center text-[10px]",
                            getStopTypeColor(stop.type, "badge"),
                          )}
                        >
                          {getStopTypeIcon(stop.type)}
                        </div>
                        {index < route.stops.length - 1 && (
                          <div className="w-0.5 flex-1 min-h-[24px] bg-palette-jungleGreen200" />
                        )}
                      </div>

                      <div className="flex-1 pb-1.5">
                        <div className="bg-white rounded p-2 border border-palette-jungleGreen200">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-xs text-palette-jungleGreen900 truncate block">
                                {stop.name}
                                <span className="text-[10px] text-palette-jungleGreen500 font-normal ml-1">
                                  ({stop.type})
                                </span>
                              </span>
                              <span className="text-[10px] text-palette-jungleGreen500 truncate block">
                                {stop.address}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <div className="text-[10px] text-palette-jungleGreen500">
                                {format(stop.plannedArrival, "HH:mm")} -{" "}
                                {format(stop.plannedDeparture, "HH:mm")}
                              </div>
                              <span className="text-[9px] text-palette-jungleGreen400">•</span>
                              <div className="text-[10px] text-palette-jungleGreen500">
                                {vol.toLocaleString()} items
                                {isActual && (
                                  <span className="text-emerald-600 text-[9px] ml-1">
                                    (actual)
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
            </div>
          )}

          {cost && (
            <div className="mt-4 pt-4 border-t border-palette-jungleGreen200">
              <CostBreakdownCard
                density="regular"
                title="Route Cost Breakdown"
                subtitle="Based on planned stops and times"
                lines={routeCostLines(cost)}
                total={cost.total_cost}
                revenue={cost.total_haulage_delivery_revenue}
                perParcel={{ cost: cost.cost_per_parcel, parcels: cost.total_forecast_volume }}
              />
            </div>
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}

/** Direct-injection routes show only their agreed price, as the wage line. */
function routeCostLines(cost: PlanningRouteCost): CostLine[] {
  if (cost.is_direct_injection_priced) {
    return [
      {
        kind: "wage",
        label: "Driver Wage",
        detail: "Agreed direct injection price",
        value: cost.driver_wage_cost,
      },
    ]
  }
  return [
    { kind: "lease", label: "Vehicle Lease", value: cost.vehicle_lease_cost },
    cost.charging_cost > 0
      ? {
          kind: "charging",
          label: "Charging Cost",
          detail: formatChargingMilesBreakdown(cost),
          value: cost.charging_cost,
        }
      : { kind: "charging", label: "Charging Cost", value: null, unavailable: "Requires 2+ stops" },
    cost.driver_wage_cost > 0
      ? { kind: "wage", label: "Driver Wage", value: cost.driver_wage_cost }
      : { kind: "wage", label: "Driver Wage", value: null, unavailable: "Requires planned times" },
  ]
}

export default PlanningRouteExpandedRow
